import {escapeSearchQueryValue, makeShopifyRequest} from "../../request";
import type {
	GetProductIdsByCourseOfferingIdAndCampusIdQuery,
	GetProductIdsByCourseOfferingIdAndCampusIdQueryVariables,
} from "../../codegen/admin.generated";

/**
 * Max course_offering metaobjects returned for one course offering id.
 * If we ever have more than 100 campuses, raise this value (or add pagination).
 */
const MAX_COURSE_OFFERING_METAOBJECTS = 100;

type CourseOfferingMetaobject =
	GetProductIdsByCourseOfferingIdAndCampusIdQuery["metaobjects"]["nodes"][number];

/**
 * Check if the offering matches the campus.
 */
function offeringMatchesCampus(
	offering: CourseOfferingMetaobject,
	campusId: string
): boolean {
	const campus = offering.campusField?.reference;
	if (!campus || campus.__typename !== "Metaobject") {
		return false;
	}
	return campus.campusIdField?.value === campusId;
}

/**
 * Get the product id from the offering.
 */
function productIdFromOffering(
	offering: CourseOfferingMetaobject
): string | null {
	for (const relation of offering.referencedBy.nodes) {
		if (relation.namespace !== "custom" || relation.key !== "course_offering") {
			continue;
		}
		const referencer = relation.referencer;
		if (referencer.__typename === "Product") {
			return referencer.id;
		}
	}
	return null;
}

/**
 * Admin can search metaobjects by field (`fields.course_offering_id`) and
 * follow `referencedBy` to a product. Campus is a metaobject reference, so
 * `campus_id` is not searchable on the offering; when `campusId` is provided,
 * matching offerings are filtered after the field search. When `campusId` is
 * null (e.g. online modalities), the first product for the offering is used.
 */
export async function findProductIdByCourseOfferingIdAndCampusId(
	courseOfferingId: string,
	campusId: string | null
): Promise<string | null> {
	const variables: GetProductIdsByCourseOfferingIdAndCampusIdQueryVariables = {
		query: `fields.course_offering_id:"${escapeSearchQueryValue(courseOfferingId)}"`,
		first: MAX_COURSE_OFFERING_METAOBJECTS,
	};

	// Get all matching offerings by course offering id
	const response =
		await makeShopifyRequest<GetProductIdsByCourseOfferingIdAndCampusIdQuery>(
			"admin",
			`#graphql
			query GetProductIdsByCourseOfferingIdAndCampusId($query: String!, $first: Int!) {
				metaobjects(type: "course_offering", first: $first, query: $query) {
					nodes {
						campusField: field(key: "campus") {
							reference {
								__typename
								... on Metaobject {
									campusIdField: field(key: "campus_id") {
										value
									}
								}
							}
						}
						referencedBy(first: 10) {
							nodes {
								namespace
								key
								referencer {
									__typename
									... on Product {
										id
									}
								}
							}
						}
					}
				}
			}
		`,
			variables
		);

	// Look for a matching campus, then find the product id
	for (const offering of response.metaobjects.nodes) {
		if (campusId && !offeringMatchesCampus(offering, campusId)) {
			continue;
		}
		const productId = productIdFromOffering(offering);
		if (productId) {
			return productId;
		}
	}
	return null;
}
